import * as Api from '@ViewModels';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'scheduledMeeting/filter';

export function useScheduledMeetingFilter({
	filterRequest,
	pageSize = 25,
	sortBy = 'published.startDate',
	sortDirection = 'desc',
	enabled = true,
	refetchOnWindowFocus = false,
	expand,
	impersonationContext,
	keepPreviousData: shouldKeepPreviousData = false,
	toastErrorMessage,
}: {
	filterRequest: Api.IScheduledMeetingFilterRequest;
	pageSize?: number;
	sortBy?: 'published.startDate' | 'name';
	sortDirection?: 'asc' | 'desc';
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	expand?: string;
	impersonationContext?: Api.IImpersonationContext;
	/** Retain the prior result while a new query key resolves (avoids blanking on filter changes). */
	keepPreviousData?: boolean;
	/** When set, a fetch failure raises a toast with this message (via the global QueryCache handler). */
	toastErrorMessage?: string;
}) {
	const userSession = useUserSession();

	return useInfiniteQuery<Api.IPagedCollection<Api.IScheduledMeeting>>({
		enabled,
		placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IScheduledMeeting>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { pageSize, pageToken: pageParam, sortBy, sortDirection, expand },
					urlPath: 'ScheduledMeeting/filter',
				}),
				'POST',
				filterRequest
			);
		},
		refetchOnWindowFocus,
		queryKey: [
			BASE_QUERY_KEY,
			filterRequest,
			pageSize,
			sortBy,
			sortDirection,
			expand,
			impersonationContext?.account?.id,
		],
		initialPageParam: '',
		meta: toastErrorMessage ? { toastErrorMessage } : undefined,
	});
}

export const invalidateAllScheduledMeetingFilters = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
